import type { ReactElement } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import qs from 'qs';

export type Theme = 'default' | 'wellbeing';

type ThemeContextShape = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

export const ThemeContext = createContext<ThemeContextShape>({
  theme: 'default',
  setTheme: () => {}
});

const getThemeFromUrl = (): Theme | undefined => {
  const parsed = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  return parsed.theme ? (parsed.theme as Theme) : undefined;
};

export const getTheme = (): Theme => {
  const urlTheme = getThemeFromUrl();
  return urlTheme || 'default';
};

export function ThemeProvider(props: React.PropsWithChildren): ReactElement {
  const [theme, setTheme] = useState<Theme>(getTheme());

  useEffect(() => {
    document?.body?.setAttribute('data-theme', theme);
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme }), [theme]);
  return <ThemeContext.Provider value={value} {...props} />;
}

export const useTheme = (): ThemeContextShape => useContext(ThemeContext);

const THEME_TITLES: Partial<Record<Theme, string>> = {
  wellbeing: 'Wellbeing @ School'
};

export const getThemeTitle = (theme: Theme): string | null => THEME_TITLES[theme] || null;
